import { DoubleSide } from 'three';
import type { ShaderMaterial } from 'three';
import type { MapDef } from '@cloudpearl/config';
import { ATMOSPHERE, BRUSH, LIGHTING, NOISE, SHADOW_FRAG_PARS, SHADOW_VERT_MAIN, SHADOW_VERT_PARS, WORLD_UNIFORMS } from '../shaders/chunks';
import type { SharedUniforms } from '../shaders/shared';
import { UNPACK, col, litMaterial } from './common';
import type { MaterialTextures } from './common';

/**
 * Smooth-shaded solids with real surface detail: triplanar normal maps on rock/cliffs/sandstone,
 * UV-mapped bark normals (cotangent frame from derivatives — no tangent attributes needed),
 * moss on up-facing surfaces, sandstone strata, cavity darkening from the maps' height channel.
 */
const SOLID_VS = /* glsl */ `
${SHADOW_VERT_PARS}
${WORLD_UNIFORMS}
${NOISE}
varying vec3 vWorldPos;
varying vec3 vNormal;
varying vec3 vColor;
varying float vRand;
varying float vLocalY;
varying vec2 vUv;
varying vec3 vObjPos;
#ifdef CLIFF
attribute float aDepth;
varying float vDepth;
#endif
void main() {
  mat4 m = modelMatrix;
  #ifdef USE_INSTANCING
    m = modelMatrix * instanceMatrix;
  #endif
  vec3 pos = position;
  #ifdef SWAY
    float hh = max(pos.y, 0.0);
    float ph = dot(m[3].xz, vec2(0.11, 0.07));
    pos.xz += uWind.xy * (sin(uTime * 1.6 + ph + pos.y * 0.8) * 0.5 + 0.6) * hh * hh * SWAY * uWind.z;
  #endif
  vec4 wPos = m * vec4(pos, 1.0);
  vec3 wNormal = normalize(mat3(m) * normal);
  vWorldPos = wPos.xyz;
  vNormal = wNormal;
  #ifdef USE_COLOR
    vColor = color;
  #else
    vColor = vec3(1.0);
  #endif
  vRand = hash12(m[3].xz * 0.0731 + m[3].y * 0.013);
  vLocalY = wPos.y - m[3].y;
  vUv = uv;
  // Object-space position, scaled — keeps triplanar texturing glued to moving/rotated props.
  vObjPos = pos * length(m[0].xyz) + vec3(vRand * 31.0, 0.0, vRand * 17.0);
  #ifdef CLIFF
    vDepth = aDepth;
  #endif
  gl_Position = projectionMatrix * viewMatrix * wPos;
  ${SHADOW_VERT_MAIN}
}
`;

const SOLID_FS = /* glsl */ `
${SHADOW_FRAG_PARS}
${WORLD_UNIFORMS}
${NOISE}
${ATMOSPHERE}
${LIGHTING}
${BRUSH}
${UNPACK}
uniform sampler2D tDetail;
uniform float uDetailScale;
uniform float uDetailStrength;
uniform vec3 uMoss;
uniform vec3 uStrataA;
uniform vec3 uStrataB;
uniform float uStrataFreq;
uniform float uRim;
uniform vec3 uGrassEdge;
varying vec3 vWorldPos;
varying vec3 vNormal;
varying vec3 vColor;
varying float vRand;
varying float vLocalY;
varying vec2 vUv;
varying vec3 vObjPos;
#ifdef CLIFF
varying float vDepth;
#endif

mat3 cotangentFrame(vec3 N, vec3 p, vec2 uv) {
  vec3 dp1 = dFdx(p);
  vec3 dp2 = dFdy(p);
  vec2 duv1 = dFdx(uv);
  vec2 duv2 = dFdy(uv);
  vec3 dp2perp = cross(dp2, N);
  vec3 dp1perp = cross(N, dp1);
  vec3 T = dp2perp * duv1.x + dp1perp * duv2.x;
  vec3 B = dp2perp * duv1.y + dp1perp * duv2.y;
  float invmax = inversesqrt(max(max(dot(T, T), dot(B, B)), 1e-12));
  return mat3(T * invmax, B * invmax, N);
}

void main() {
  vec3 n0 = normalize(vNormal);
  #ifdef DOUBLE_SIDED
    vec3 Vd = normalize(cameraPosition - vWorldPos);
    n0 = dot(n0, Vd) < 0.0 ? -n0 : n0;
  #endif
  vec3 n;
  float cavity;
  #ifdef UVMAP
    vec4 dt = texture2D(tDetail, vUv * vec2(uDetailScale, uDetailScale * 0.5));
    vec3 tn = unpackN(dt);
    tn.xy *= uDetailStrength;
    n = normalize(cotangentFrame(n0, vWorldPos, vUv) * normalize(tn));
    cavity = dt.a;
  #else
    n = triNormal(tDetail, vObjPos, n0, uDetailScale, uDetailStrength);
    cavity = triHeight(tDetail, vObjPos, n0, uDetailScale);
  #endif

  vec4 br = triBrush(vObjPos, n0, 0.35);
  vec3 albedo = vColor * (0.9 + vRand * 0.2);
  albedo *= 0.8 + cavity * 0.28;

  #ifdef STRATA
    float band = vLocalY * uStrataFreq + vnoise(vWorldPos.xz * 0.08 + vRand * 10.0) * 1.3 + (br.a - 0.5) * 0.9;
    float f = fract(band);
    albedo = mix(albedo, uStrataA, smoothstep(0.32, 0.48, f) * smoothstep(0.86, 0.7, f) * 0.55);
    albedo = mix(albedo, uStrataB, step(0.86, fract(band * 0.41 + 0.17)) * 0.45);
  #endif

  #ifdef CLIFF
    float bandC = vWorldPos.y * 0.14 + vnoise(vWorldPos.xz * 0.05) * 2.0 + (br.a - 0.5);
    albedo = mix(albedo, uStrataA, smoothstep(0.4, 0.55, fract(bandC)) * 0.4);
    albedo = mix(albedo, uStrataB, smoothstep(0.3, 1.0, vDepth) * 0.4);
    // A grassy lip draping over the coastline.
    float lip = 1.0 - smoothstep(0.0, 0.035 + br.r * 0.03, vDepth);
    albedo = mix(albedo, uGrassEdge * (0.75 + br.b * 0.3), lip);
  #endif

  #ifdef MOSS
    float mm = smoothstep(0.38, 0.78, n.y + (br.a - 0.5) * 0.6 + (vnoise(vWorldPos.xz * 0.9) - 0.5) * 0.4);
    albedo = mix(albedo, uMoss * (0.7 + cavity * 0.5), mm);
  #endif

  #ifdef ROOF
    float tiles = smoothstep(0.0, 0.15, fract(vUv.x * 9.0)) * (0.82 + 0.18 * smoothstep(0.0, 0.2, fract(vUv.y * 7.0)));
    albedo *= 0.7 + tiles * 0.34;
  #endif

  float shadow = getShadowMask() * cloudShade(vWorldPos);
  vec3 V = normalize(cameraPosition - vWorldPos);
  vec3 c = paintLight(albedo, n, shadow, mix(0.65, 1.0, cavity));
  c += hemiAmbient(n) * fresnelRim(n, V, 3.5) * uRim * albedo;
  // A touch of specular so stone and bark aren't chalk.
  float spec = pow(max(dot(normalize(uSunDir + V), n), 0.0), 24.0);
  c += uSunColor * spec * 0.025 * shadow * cavity;
  gl_FragColor = vec4(applyFog(c, vWorldPos), 1.0);
}
`;

export type SolidStyle = 'stone' | 'mossStone' | 'sandstone' | 'cliff' | 'bark' | 'mossWood' | 'plaster' | 'roof' | 'cactus' | 'grassTop' | 'tuft';

export function createSolidMaterial(shared: SharedUniforms, map: MapDef, tex: MaterialTextures, style: SolidStyle): ShaderMaterial {
  const p = map.palette;
  const defines: Record<string, string | number | boolean> = {};
  let detail = tex.rock;
  let detailScale = 0.32;
  let strength = 0.7;
  let rim = 0.12;
  let side: typeof DoubleSide | undefined;
  switch (style) {
    case 'mossStone':
      defines['MOSS'] = 1;
      break;
    case 'sandstone':
      defines['STRATA'] = 1;
      detailScale = 0.18;
      strength = 0.8;
      break;
    case 'cliff':
      defines['CLIFF'] = 1;
      detailScale = 0.07;
      strength = 1.2;
      break;
    case 'bark':
    case 'mossWood':
      defines['UVMAP'] = 1;
      if (style === 'mossWood') defines['MOSS'] = 1;
      detail = tex.bark;
      detailScale = 1;
      strength = 1.3;
      break;
    case 'cactus':
      defines['UVMAP'] = 1;
      defines['SWAY'] = 0.002;
      detail = tex.bark;
      detailScale = 2;
      strength = 0.45;
      break;
    case 'roof':
      defines['ROOF'] = 1;
      strength = 0.4;
      break;
    case 'plaster':
      detail = tex.ground;
      detailScale = 0.5;
      strength = 0.35;
      break;
    case 'grassTop':
      defines['MOSS'] = 1;
      detail = tex.ground;
      detailScale = 0.2;
      break;
    case 'tuft':
      defines['SWAY'] = 0.06;
      detail = tex.ground;
      strength = 0.1;
      rim = 0.25;
      side = DoubleSide;
      break;
    default:
      break;
  }
  return litMaterial(
    shared,
    {
      tBrush: { value: tex.brush },
      tDetail: { value: detail },
      uDetailScale: { value: detailScale },
      uDetailStrength: { value: strength },
      uMoss: col(p.moss),
      uStrataA: col(style === 'cliff' ? p.cliffDark : p.rockDark),
      uStrataB: col(style === 'cliff' ? p.cliffDark : p.accent),
      uStrataFreq: { value: style === 'sandstone' ? 0.55 : 0.3 },
      uRim: { value: rim },
      uGrassEdge: col(p.groundA),
    },
    { name: `solid-${style}`, vertexShader: SOLID_VS, fragmentShader: SOLID_FS, defines, vertexColors: true, side },
  );
}
